import { useEffect, useState } from 'react';
import { Eye, Mail, Phone, X, MapPin, Calendar, Ban, Trash2, CheckCircle } from '../../components/icons';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { extractErr } from '../../api/error-message';
import { useAuthStore } from '../../store/auth-store';
import { DataTable, type Column } from '../../components/data-table';
import { StatusBadge } from '../../components/status-badge';
import { PageHeader } from '../../components/page-header';
import { StatCard } from '../../components/stat-card';
import { Symbol } from '../../components/symbol';
import { useConfirm } from '../../components/confirm-dialog';
import { useDebouncedValue } from '../../components/table-search';

// Wired to GET /admin/customers — real shoppers with computed orders count,
// lifetime spend and a derived loyalty tier (PLATINUM/GOLD/SILVER/BRONZE).
// Paged, searched and filtered on the server: the API used to cap the list at
// 1,000 customers, and this page could only filter what it had loaded.
type Tier = 'PLATINUM' | 'GOLD' | 'SILVER' | 'BRONZE';

const PER_PAGE = 20;
const STATUS_TABS = ['ALL', 'ACTIVE', 'BLOCKED'] as const;
type StatusTab = (typeof STATUS_TABS)[number];
const TIER_OPTIONS: Array<Tier | 'ALL'> = ['ALL', 'PLATINUM', 'GOLD', 'SILVER', 'BRONZE'];

interface Customer {
  id: string;
  code: string;
  fullName: string;
  email: string | null;
  phoneE164: string;
  city: string | null;
  status: string;
  /** Why an admin blocked the account; null unless BLOCKED. */
  blockedReason?: string | null;
  ordersCount: number;
  totalSpent: number;
  tier: Tier;
  joinedAt: string;
}

interface CustomersResponse {
  items: Customer[];
  total: number;
  page: number;
  limit: number;
  summary: {
    total: number;
    newThisMonth: number;
    avgLifetimeValue: number;
    tierCounts: Record<Tier, number>;
  };
}

const tierLabel: Record<Tier, string> = {
  PLATINUM: 'Platinum',
  GOLD: 'Gold',
  SILVER: 'Silver',
  BRONZE: 'Bronze',
};
const tierClass: Record<Tier, string> = {
  PLATINUM: 'badge-jamun',
  GOLD: 'badge-warning',
  SILVER: '',
  BRONZE: 'badge-outline',
};

function inr(n: number): string {
  return `₹${n.toLocaleString('en-IN')}`;
}
function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
function initials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

const columns: Column<Customer>[] = [
  {
    key: 'code',
    label: 'Customer ID',
    render: (r) => <span className="sku text-ink-700">{r.code}</span>,
  },
  {
    key: 'fullName',
    label: 'Customer',
    render: (r) => (
      <div className="flex items-center gap-2.5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-admin-soft text-[12.5px] font-bold text-admin-ink">
          {initials(r.fullName)}
        </span>
        <div>
          <p className="font-semibold text-ink-900">{r.fullName}</p>
          <p className="text-[12.5px] text-ink-500">{r.city ?? '—'}</p>
        </div>
      </div>
    ),
  },
  { key: 'phoneE164', label: 'Phone', render: (r) => <span className="whitespace-nowrap text-ink-700">{r.phoneE164}</span> },
  { key: 'email', label: 'Email', render: (r) => <span className="text-[13.5px] text-ink-700">{r.email ?? '—'}</span> },
  { key: 'ordersCount', label: 'Orders' },
  {
    key: 'totalSpent',
    label: 'Total spent',
    align: 'right',
    render: (r) => <span className="font-tabular font-semibold text-ink-900">{inr(r.totalSpent)}</span>,
  },
  {
    key: 'tier',
    label: 'Tier',
    render: (r) => (
      <span className={`badge ${tierClass[r.tier]}`}>
        {tierLabel[r.tier]}
      </span>
    ),
  },
  { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  { key: 'joinedAt', label: 'Joined', render: (r) => fmtDate(r.joinedAt) },
];

export function CustomersPage() {
  const canSuspend = useAuthStore((s) => s.hasScope('customer.suspend'));
  const canDelete = useAuthStore((s) => s.hasScope('customer.write'));
  const [data, setData] = useState<CustomersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Customer | null>(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<StatusTab>('ALL');
  const [tier, setTier] = useState<Tier | 'ALL'>('ALL');
  const { confirm, prompt } = useConfirm();
  // Server-side search over every column the table shows (ID, name, city,
  // phone, email, orders, spend, tier, status, joined) — see customerSearchSql.
  const [search, setSearch] = useState('');
  const q = useDebouncedValue(search.trim(), 300);
  const [reloadKey, setReloadKey] = useState(0);
  const load = () => setReloadKey((k) => k + 1);

  // A new filter is a new result set — back to page 1.
  useEffect(() => { setPage(1); }, [status, tier, q]);

  // Guard against out-of-order responses when filters change quickly.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .get<CustomersResponse>('/admin/customers', {
        params: {
          page,
          limit: PER_PAGE,
          ...(q ? { q } : {}),
          ...(status === 'ALL' ? {} : { status }),
          ...(tier === 'ALL' ? {} : { tier }),
        },
      })
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setError('Could not load customers. Please try again.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [page, status, tier, q, reloadKey]);

  const setBlocked = async (c: Customer, block: boolean) => {
    let body: { reason: string } | undefined;
    if (block) {
      const reason = await prompt({
        title: `Block ${c.fullName}?`,
        description: 'They’re signed out everywhere straight away and can’t sign in until unblocked. Their orders are kept.',
        label: 'Reason for blocking',
        placeholder: 'e.g. Repeated fake COD orders',
        help: 'Kept in the audit log. At least 3 characters.',
        required: true,
        multiline: true,
        confirmLabel: 'Block customer',
        tone: 'danger',
      });
      if (reason === null) return;
      if (reason.length < 3) {
        toast.error('Please give a reason (at least 3 characters).');
        return;
      }
      body = { reason };
    } else if (
      !(await confirm({
        title: `Unblock ${c.fullName}?`,
        description: 'They’ll be able to sign in and place orders again.',
        confirmLabel: 'Unblock customer',
        tone: 'primary',
      }))
    ) {
      return;
    }
    try {
      const { data: res } = await api.post<{ status: string; blockedReason: string | null }>(
        `/admin/customers/${c.id}/${block ? 'block' : 'unblock'}`,
        body,
      );
      toast.success(`${c.fullName} ${block ? 'blocked' : 'unblocked'}`);
      if (selected?.id === c.id) setSelected({ ...c, status: res.status, blockedReason: res.blockedReason });
      load();
    } catch (err) {
      toast.error(extractErr(err));
    }
  };

  const remove = async (c: Customer) => {
    if (
      !(await confirm({
        title: `Delete ${c.fullName}?`,
        description:
          'They’re removed from the customer list, signed out and can no longer sign in. Order history is kept. This can’t be undone.',
        confirmLabel: 'Delete customer',
        tone: 'danger',
      }))
    ) {
      return;
    }
    try {
      await api.delete(`/admin/customers/${c.id}`);
      toast.success(`${c.fullName} deleted`);
      if (selected?.id === c.id) setSelected(null);
      load();
    } catch (err) {
      toast.error(extractErr(err));
    }
  };

  const items = data?.items ?? [];
  const counts = data?.summary.tierCounts;

  const summary = [
    { label: 'Total customers', value: (data?.summary.total ?? 0).toLocaleString(), sub: 'Registered users', icon: 'group' },
    {
      label: 'Platinum / Gold',
      value: `${counts?.PLATINUM ?? 0} / ${counts?.GOLD ?? 0}`,
      sub: 'Loyalty tiers',
      icon: 'workspace_premium',
    },
    { label: 'Avg. lifetime value', value: inr(data?.summary.avgLifetimeValue ?? 0), sub: 'Per customer', icon: 'payments' },
    { label: 'New this month', value: String(data?.summary.newThisMonth ?? 0), sub: 'This month', icon: 'person_add' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Customers" description="Registered shoppers and loyalty tiers." />

      {error && (
        <div role="alert" className="alert alert-danger items-center justify-between">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => load()}
            className="btn btn-danger-outline btn-sm"
          >
            Retry
          </button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {summary.map((s) => (
          <StatCard
            key={s.label}
            label={s.label}
            value={s.value}
            subValue={s.sub}
            icon={<Symbol name={s.icon} size={20} />}
          />
        ))}
      </div>

      {/* Server-side filters: status tabs and tier. Search is the table's own box. */}
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setStatus(t)}
            aria-pressed={status === t}
            className="fchip"
          >
            {t === 'ALL' ? 'All' : t.charAt(0) + t.slice(1).toLowerCase()}
          </button>
        ))}
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value as Tier | 'ALL')}
          aria-label="Tier"
          className="input input-sm w-auto min-w-[140px]"
        >
          {TIER_OPTIONS.map((t) => (
            <option key={t} value={t}>{t === 'ALL' ? 'All tiers' : tierLabel[t]}</option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        data={items}
        rowKey={(r) => r.id}
        // Skeleton on the first load only: a refetch while typing must not
        // unmount the table's search box mid-word.
        loading={loading && !data}
        perPage={PER_PAGE}
        serverPagination={{ page, totalItems: data?.total ?? 0, onPageChange: setPage }}
        searchPlaceholder="Search by name, ID, phone, email, city, tier, spend or status"
        searchValue={search}
        onSearchChange={setSearch}
        exportName="customers"
        onRowClick={(r) => setSelected(r)}
        actions={(r) => (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              title="View profile"
              onClick={() => setSelected(r)}
              aria-label="View profile"
              className="btn btn-quiet btn-icon btn-sm"
            >
              <Eye size={16} />
            </button>
            <button
              type="button"
              title="Send email"
              onClick={() =>
                toast.success(`Email sent to ${r.fullName}`, { description: `Dispatched to ${r.email ?? 'no email'}` })
              }
              aria-label="Send email"
              className="btn btn-quiet btn-icon btn-sm"
            >
              <Mail size={16} />
            </button>
            {canSuspend && (r.status === 'ACTIVE' || r.status === 'BLOCKED') && (
              <button
                type="button"
                title={r.status === 'BLOCKED' ? 'Unblock customer' : 'Block customer'}
                onClick={(e) => {
                  e.stopPropagation();
                  void setBlocked(r, r.status !== 'BLOCKED');
                }}
                aria-label={r.status === 'BLOCKED' ? 'Unblock customer' : 'Block customer'}
                className={`btn btn-quiet btn-icon btn-sm ${
                  r.status === 'BLOCKED' ? '!text-success-ink' : 'hover:!text-danger-ink'
                }`}
              >
                {r.status === 'BLOCKED' ? <CheckCircle size={16} /> : <Ban size={16} />}
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                title="Delete customer"
                onClick={(e) => {
                  e.stopPropagation();
                  void remove(r);
                }}
                aria-label="Delete customer"
                className="btn btn-quiet btn-icon btn-sm hover:!text-danger-ink"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        )}
      />

      {selected && (
        <CustomerProfile
          customer={selected}
          canSuspend={canSuspend}
          onToggleBlock={() => void setBlocked(selected, selected.status !== 'BLOCKED')}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function CustomerProfile({
  customer, canSuspend, onToggleBlock, onClose,
}: {
  customer: Customer;
  canSuspend: boolean;
  onToggleBlock: () => void;
  onClose: () => void;
}) {
  const avgOrder = customer.ordersCount ? Math.round(customer.totalSpent / customer.ordersCount) : 0;
  const stats = [
    { label: 'Total orders', value: String(customer.ordersCount) },
    { label: 'Total spent', value: inr(customer.totalSpent) },
    { label: 'Avg. order value', value: inr(avgOrder) },
    { label: 'Status', value: customer.status.charAt(0) + customer.status.slice(1).toLowerCase() },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-scrim" onClick={onClose}>
      <aside
        className="flex h-full w-[min(560px,100vw)] flex-col bg-surface-card shadow-elev-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-surface-line px-6 pb-4 pt-5">
          <h2 className="font-display text-drawer-title text-ink-900">Customer profile</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="btn btn-quiet btn-icon btn-sm -mr-2"
          >
            <X size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div className="flex items-start gap-4">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-admin-soft text-[19px] font-bold text-admin-ink">
              {initials(customer.fullName)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-[19px] font-semibold leading-tight text-ink-900">{customer.fullName}</h3>
                <span className={`badge ${tierClass[customer.tier]}`}>
                  {tierLabel[customer.tier]}
                </span>
              </div>
              <p className="sku mt-1 text-ink-500">{customer.code}</p>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[13.5px] text-ink-700">
                <span className="flex items-center gap-1">
                  <MapPin size={14} className="text-ink-500" />
                  {customer.city ?? '—'}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar size={14} className="text-ink-500" />
                  Joined {fmtDate(customer.joinedAt)}
                </span>
              </div>
            </div>
          </div>

          <div className="card space-y-2.5 bg-surface-sunk p-4">
            <p className="mb-1 text-[12.5px] font-semibold text-ink-500">Contact details</p>
            <div className="flex items-center gap-2.5">
              <Phone size={16} className="text-admin" />
              <span className="text-[14px] text-ink-900">{customer.phoneE164}</span>
            </div>
            <div className="flex items-center gap-2.5">
              <Mail size={16} className="text-admin" />
              <span className="text-[14px] text-ink-900">{customer.email ?? '—'}</span>
            </div>
          </div>

          {customer.status === 'BLOCKED' && (
            <div className="alert alert-danger flex-col gap-0.5">
              <p className="font-semibold">Blocked</p>
              <p className="text-[13.5px] text-ink-700">{customer.blockedReason || 'No reason recorded'}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {stats.map((s) => (
              <div key={s.label} className="card p-3.5">
                <p className="text-[12.5px] text-ink-500">{s.label}</p>
                <p className="mt-0.5 font-tabular text-[19px] font-semibold text-ink-900">{s.value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-2.5 border-t border-surface-line px-6 py-3.5">
          {canSuspend && customer.status === 'BLOCKED' && (
            <button
              type="button"
              onClick={onToggleBlock}
              className="btn btn-secondary"
            >
              <CheckCircle size={16} /> Unblock
            </button>
          )}
          {canSuspend && customer.status === 'ACTIVE' && (
            <button
              type="button"
              onClick={onToggleBlock}
              className="btn btn-danger-outline"
            >
              <Ban size={16} /> Block
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              toast.success(`Email sent to ${customer.fullName}`, { description: `Dispatched to ${customer.email ?? 'no email'}` });
              onClose();
            }}
            className="btn btn-primary"
          >
            <Mail size={16} /> Send email
          </button>
        </div>
      </aside>
    </div>
  );
}
